import { FC, useState } from "react";

import Stack from "@mui/material/Stack";

import { AmountField } from "../../RateFields/AmountField/AmountField";
import { DescriptionField } from "../../RateFields/DescriptionField/DescriptionField";
import { HiddenChip } from "../../RateFields/HiddenChip/HiddenChip";
import { MaxPerOrderField } from "../../RateFields/MaxPerOrderField/MaxPerOrderField";
import { MinPerOrderField } from "../../RateFields/MinPerOrderField/MinPerOrderField";
import { NameField } from "../../RateFields/NameField/NameField";
import { RateImagesField } from "../../RateFields/RateImagesField/RateImagesField";
import { ShopVariantsField } from "../../RateFields/ShopVariantsField/ShopVariantsField";
import { VisibilityToggleField } from "../../RateFields/VisibilityToggleField/VisibilityToggleField";
import { ExtrasSection } from "../../shared/ExtrasSection/ExtrasSection";
import { CategoriesRow } from "../../shared/RateOptions/OptionRows/CategoriesRow";
import { ScheduleRow } from "../../shared/RateOptions/OptionRows/ScheduleRow/ScheduleRow";
import { MemberOnlyRow } from "../../shared/RateOptions/OptionRows/MemberOnlyRow";
import { PayWhatYouCanRow } from "../../shared/RateOptions/OptionRows/PayWhatYouCanRow";
import { TaxReceiptRow } from "../../shared/RateOptions/OptionRows/TaxReceiptRow/TaxReceiptRow";
import { useShowTaxReceiptRow } from "../../shared/RateOptions/OptionRows/TaxReceiptRow/useShowTaxReceiptRow";
import { TaxRow } from "../../shared/RateOptions/OptionRows/TaxRow/TaxRow";
import { useShowTaxRow } from "../../shared/RateOptions/OptionRows/TaxRow/useShowTaxRow";
import { RateOptionsGroup } from "../../shared/RateOptions/RateOptionsGroup";
import { RateCardShell } from "../../shared/RateCardShell/RateCardShell";
import { RateFieldsRow } from "../../shared/RateFieldsRow/RateFieldsRow";
import { RateSectionDivider } from "../../shared/RateSectionDivider/RateSectionDivider";
import { RateStatusChip } from "../../shared/RateStatusChip/RateStatusChip";

import { useTranslate } from "@/common/hooks/useTranslate";

interface ShopRateProps {
  totalCardNumber: number;
  shouldDisplayDeleteButton?: boolean;
  shouldConfirmDelete?: boolean;
  onDelete: (index: number) => void;
  onDuplicate: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  questionsArchivedOnDelete?: string[];
}

export const ShopRate: FC<ShopRateProps> = (props) => {
  const { t } = useTranslate();
  const showTaxReceiptRow = useShowTaxReceiptRow();
  const showTaxRow = useShowTaxRow();

  const [isInventoryOpen, setIsInventoryOpen] = useState(false);

  return (
    <RateCardShell
      {...props}
      extraActions={<VisibilityToggleField />}
      statusChip={
        <>
          <RateStatusChip />
          <HiddenChip />
        </>
      }
      duplicateTooltip={t("dashboard", "ticketing.shopItem.duplicate")}
      deleteTooltip={t("dashboard", "ticketing.shopItem.delete")}
    >
      <Stack spacing={2}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
          <Stack sx={{ flexShrink: 0 }}>
            <RateImagesField />
          </Stack>
          <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
            <NameField />
            <AmountField />
            <DescriptionField />
          </Stack>
        </Stack>

        <RateSectionDivider label={t("dashboard", "ticketing.sectionDivider.variantsAndInventory")} />

        <ShopVariantsField isInventoryOpen={isInventoryOpen} onInventoryOpenChange={setIsInventoryOpen} />

        <ExtrasSection
          mode="always-open"
          alwaysOpenDividerLabel={t("dashboard", "ticketing.sectionDivider.additionalOptions")}
        >
          <RateOptionsGroup
            title={t("dashboard", "ticketing.rateOptionsGroup.capacity")}
            beforeRows={
              <RateFieldsRow>
                <MinPerOrderField />
                <MaxPerOrderField />
              </RateFieldsRow>
            }
          >
            <ScheduleRow />
            <MemberOnlyRow />
          </RateOptionsGroup>
          <RateOptionsGroup title={t("dashboard", "ticketing.rateOptionsGroup.ticketType")}>
            <PayWhatYouCanRow />
            <CategoriesRow />
          </RateOptionsGroup>
          {(showTaxReceiptRow || showTaxRow) && (
            <RateOptionsGroup title={t("dashboard", "ticketing.rateOptionsGroup.taxesAndReceipts")}>
              {showTaxReceiptRow && <TaxReceiptRow />}
              {showTaxRow && <TaxRow />}
            </RateOptionsGroup>
          )}
        </ExtrasSection>
      </Stack>
    </RateCardShell>
  );
};
